import { type Account, AccountStatus } from '@services/dto/account';
import { isProjectScopedOn } from '@services/projectScopedGuest';

/**
 * Which of a person's accounts they may actually be shown.
 *
 * Ready or Vetted, which is what the server means by a live account: it is the same pair the
 * provider listings use (AccountRepository.GetProviderAccountIdsAsync). Ready alone was a trap -
 * Vetted is the status an administrator sets to say a firm has been checked, and setting it
 * emptied that firm's own account list, which sends them to onboarding, which sends them back,
 * forever. Being approved harder should not log you out.
 *
 * Deferred is the third, and it is the one an invited firm lives in. It is the shell raised for a
 * company nobody has claimed yet - a shell for someone else, as far as an ordinary member is
 * concerned, so it stays out of their list. For the guest who was invited into it, it is the only
 * account they have, and leaving it out gave them an empty list. An empty list is no current
 * account, so the account-users call never fires, so rights never resolve, so Dashboard's
 * `!rights` gate holds the loading spinner up and never lets go. That is what a firm was landing
 * on the moment it accepted an invitation.
 *
 * Asked here, on the way out of the store, rather than on the way in. The answer depends on who
 * is asking, which is fetched alongside the accounts rather than before them - deciding it at load
 * time would decide it before we know, and the soft-load dedupe means the question is never put
 * again.
 *
 * Asked per account, because that is the shape of the answer. Somebody can be a guest on one
 * company and staff at another, and the account's own roster says which - a fact about the person
 * alone could only ever be wrong for one of them.
 * @param accounts - What the server returned, unfiltered. Undefined while it is still loading.
 * @param userId - Who is asking.
 * @returns The visible subset, or undefined while loading.
 */
export const visibleAccounts = (accounts: Account[] | undefined, userId?: string | null): Account[] | undefined =>
  accounts?.filter(
    (a) =>
      a.status === AccountStatus.Ready ||
      a.status === AccountStatus.Vetted ||
      (a.status === AccountStatus.Deferred && isProjectScopedOn(a, userId)),
  );
